import {Input} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {DEFAULT_MASK} from '@yandex-delivery/core/phones/selectors'
import {getDigits} from '@yandex-delivery/core/phones/utils'
import {useController, useFormContext} from '@yandex-delivery/form'
import {identity, pipe} from '@yandex-delivery/utils'
import {templateFormatter} from 'input-format'
import React, {useCallback} from 'react'

import {useUserSettingsContext} from 'src/shared/contexts'

import type {PhoneInputProps} from './PhoneInput.types'

export function PhoneInput({
  name,
  helperText,
  'data-testid': dataTestId,
  onFocus,
  placeholder,
  ...inputProps
}: PhoneInputProps): React.ReactElement {
  const t = useI18n()
  const {normalizePhone = identity, getPhoneMask} = useUserSettingsContext()
  const {control} = useFormContext()
  const {
    field: {value = '', ref, onBlur, onChange},
    fieldState: {invalid, error},
  } = useController({control, name, defaultValue: ''})

  const formatPhone = useCallback(
    (inputValue: string) => {
      const valueDigits = getDigits(inputValue)
      const template = getPhoneMask?.(valueDigits) || DEFAULT_MASK
      const formattedValue = templateFormatter(template)(valueDigits)

      return formattedValue.text
    },
    [getPhoneMask],
  )

  const handleChange = useCallback(
    (eventOrValue: string | React.ChangeEvent<HTMLInputElement>) => {
      const currentValue = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value

      pipe(currentValue, formatPhone, normalizePhone, onChange)
    },
    [normalizePhone, onChange, formatPhone],
  )

  return (
    <div ref={ref}>
      <Input
        data-testid={dataTestId}
        error={invalid}
        helperText={error?.message || helperText}
        mask={getPhoneMask?.(value)}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder === undefined ? t('cargo.create.contact.phone', 'Телефон') : placeholder}
        type="tel"
        value={pipe(value, formatPhone, getDigits)}
        {...inputProps}
      />
    </div>
  )
}
